import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * ConfirmationDialog component props
 */
interface ConfirmationDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'danger' | 'primary';
  /** Optional third action next to the confirm button (e.g. "Save" on a
   *  discard-changes dialog, so rescuing the edits is one tap instead of
   *  cancel-then-save — Paulo 2026-08-18). Rendered in the app's green. */
  extraText?: string;
  onExtra?: () => void;
}

/**
 * ConfirmationDialog component
 * Reusable confirmation dialog for destructive actions like deletions
 * Includes accessible design and proper focus management
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonStyle = 'danger',
  extraText,
  onExtra
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter') {
      onConfirm();
    }
  };

  /**
   * Focus management on mount
   */
  useEffect(() => {
    if (confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, []);

  /**
   * Prevent background scrolling when dialog is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  /**
   * Get button styles based on type
   */
  const getConfirmButtonStyle = () => {
    const baseStyle = {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    };

    if (confirmButtonStyle === 'danger') {
      return {
        ...baseStyle,
        backgroundColor: '#dc3545',
        color: 'white'
      };
    }

    // Brand green (docs/codebase/ui-conventions.md), same as the extra action.
    return {
      ...baseStyle,
      backgroundColor: '#157a55',
      color: 'white'
    };
  };

  const dialogContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // z-index must beat drawers/modals (e.g., settings modal at 2001)
        zIndex: 3000
      }}
      onClick={onCancel} // Close on backdrop click
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '2rem',
          width: '90%',
          maxWidth: '400px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking dialog content
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Dialog Header */}
        <div style={{ marginBottom: '1rem' }}>
          <h2
            id="dialog-title"
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: '#333'
            }}
          >
            {title}
          </h2>
        </div>

        {/* Dialog Message */}
        <div
          id="dialog-message"
          style={{
            marginBottom: '1.5rem',
            color: '#666',
            lineHeight: '1.5'
          }}
        >
          {message}
        </div>

        {/* Dialog Actions */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            style={getConfirmButtonStyle()}
          >
            {confirmText}
          </button>
          {extraText && onExtra && (
            <button
              onClick={onExtra}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#157a55',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {extraText}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Render in a portal so the overlay escapes parent containers (e.g., Drawer)
  // and covers the full viewport on desktop.
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(dialogContent, document.body);
  }

  // Fallback for non-DOM environments
  return dialogContent;
};

export default ConfirmationDialog; 